import React, { useCallback, useRef, useState } from 'react';
import { FiFileText } from 'react-icons/fi';
import '../../styles/QuoteProposal.css';

const svgBase = { fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };

const FiUploadCloud: React.FC<{ size?: number; className?: string }> = ({ size = 28, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" className={className} {...svgBase}>
    <polyline points="16 16 12 12 8 16" /><line x1="12" y1="12" x2="12" y2="21" />
    <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
  </svg>
);

const FiCheck: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...svgBase}>
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

export interface UploadDocumentZoneProps {
  onFileDrop: (file: File) => void;
  onInvalidFile?: (file: File) => void;
  fileName?: string;
  fileURL?: string;
  uploading?: boolean;
  progress?: number;
  compact?: boolean;
  accept?: string;
}

const UploadDocumentZone: React.FC<UploadDocumentZoneProps> = ({
  onFileDrop,
  onInvalidFile,
  fileName,
  fileURL,
  uploading = false,
  progress = 0,
  compact = false,
  accept = '.pdf,application/pdf',
}) => {
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const isPdf = (file: File) =>
    file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');

  const handleFile = useCallback((file: File) => {
    if (!isPdf(file)) {
      onInvalidFile?.(file);
      return;
    }
    onFileDrop(file);
  }, [onFileDrop, onInvalidFile]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    if (inputRef.current) inputRef.current.value = '';
  }, [handleFile]);

  /* ── Uploading state ── */
  if (uploading) {
    return (
      <div className={`qp-upload-zone ${compact ? 'qp-upload-zone--small' : ''}`}>
        <div className="qp-upload-loading">
          <div className="qp-spinner" />
          <span className="qp-upload-zone__text">
            {progress >= 100 ? 'Processing…' : 'Uploading…'}
          </span>
          {progress < 100 && (
            <div className="qp-upload-progress">
              <div className="qp-upload-progress__bar" style={{ width: `${progress}%` }} />
            </div>
          )}
        </div>
      </div>
    );
  }

  /* ── Uploaded / file ready state ── */
  if (fileName) {
    return (
      <div className="qp-files__item">
        <FiFileText size={14} className="qp-files__icon" />
        <span className="qp-files__name">{fileName}</span>
        <span className="qp-files__uploaded" title="Uploaded"><FiCheck size={11} /></span>
        {fileURL && (
          <a
            href={fileURL}
            target="_blank"
            rel="noopener noreferrer"
            className="qp-files__view"
          >
            View
          </a>
        )}
      </div>
    );
  }

  /* ── Drop zone ── */
  return (
    <div
      className={`qp-upload-zone${dragActive ? ' qp-upload-zone--active' : ''}${compact ? ' qp-upload-zone--small' : ''}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleChange}
        style={{ display: 'none' }}
      />
      <FiUploadCloud size={compact ? 22 : 28} className="qp-upload-zone__icon" />
      <p className="qp-upload-zone__text">
        {compact ? 'Drop PDF or' : 'Drag & drop a PDF or'}
      </p>
      <button
        type="button"
        className="qp-upload-zone__btn glass-btn glass-btn-primary"
        onClick={() => inputRef.current?.click()}
      >
        Browse
      </button>
      <span className="qp-upload-zone__note">PDF only · max 30 MB</span>
    </div>
  );
};

export default React.memo(UploadDocumentZone);
